/**
 * Initialize banner background video
 * Supports MP4, YouTube, and Vimeo with autoplay
 * Video is displayed after header opacity reaches 1, or after spline loads if no loader
 */
function initBannerVideo() {
    const videoContainer = document.getElementById('banner-video-container');
    if (!videoContainer) return;
    
    // Get video configuration from data attributes on banner section
    const bannerSection = document.querySelector('.banner') || document.querySelector('.banner-inner');
    if (!bannerSection) return;
    
    const videoType = bannerSection.getAttribute('data-video-type');
    const videoSrc = bannerSection.getAttribute('data-video-src');
    const videoId = bannerSection.getAttribute('data-video-id');
    
    // If no video configuration, don't initialize
    if (!videoType || (!videoSrc && !videoId)) return;
    
    // Cache lowercase video type and spline containers
    const videoTypeLower = videoType.toLowerCase();
    const bannerSplineContainers = document.querySelectorAll('.banner-spline-container');
    const hasGSAP = typeof gsap !== 'undefined';
    const PLAY_DELAY = 100;
    const FALLBACK_TIMEOUT = 3000;
    
    // Check if any spline container has a valid URL - if so, show video immediately
    let hasValidBannerSpline = false;
    bannerSplineContainers.forEach(container => {
        const bannerSplineUrl = container.getAttribute('data-spline-url');
        if (bannerSplineUrl && bannerSplineUrl.trim() !== '') {
            hasValidBannerSpline = true;
        }
    });
    
    // Hide video container initially - will show after header opacity is 1 or spline loads
    // But if no spline URL, show immediately
    if (hasValidBannerSpline) {
        videoContainer.style.display = 'none';
    }
    
    // Function to hide all splines with fade animation (no scaling)
    const hideSpline = () => {
        if (bannerSplineContainers.length === 0) return;
        
        bannerSplineContainers.forEach(bannerSplineContainer => {
            if (hasGSAP) {
                // Use GSAP to fade out the spline
                gsap.to(bannerSplineContainer, {
                    opacity: 0,
                    duration: 0.8,
                    ease: 'power2.out',
                    onComplete: () => {
                        bannerSplineContainer.style.display = 'none';
                    }
                });
            } else {
                // Fallback: use CSS transition if GSAP is not available
                bannerSplineContainer.style.transition = 'opacity 0.8s ease-out';
                bannerSplineContainer.style.opacity = '0';
                setTimeout(() => {
                    bannerSplineContainer.style.display = 'none';
                }, 800);
            }
        });
    };
    
    let videoElement = null;
    let youtubePlayer = null;
    
    // Function to show video and play it (optimized)
    const showAndPlayVideo = () => {
        videoContainer.style.display = '';
        hideSpline();
        playVideoWhenDisplayed();
    };
    
    // Function to play video when displayed
    const playVideoWhenDisplayed = () => {
        if (videoTypeLower === 'youtube') {
            if (youtubePlayer) {
                // Use YouTube API to play video
                try {
                    youtubePlayer.playVideo();
                } catch (err) {
                    console.warn('Error playing YouTube video:', err);
                }
            } else {
                // Fallback: if using direct iframe, update URL to trigger autoplay
                const iframe = videoContainer.querySelector('iframe');
                if (iframe?.src) {
                    // Update iframe src to include autoplay=1
                    const url = new URL(iframe.src);
                    url.searchParams.set('autoplay', '1');
                    iframe.src = url.toString();
                }
            }
        } else if (videoTypeLower === 'mp4' && videoElement) {
            // Play MP4 video
            videoElement.play().catch(err => {
                console.warn('Video autoplay prevented:', err);
            });
        } else if (videoTypeLower === 'vimeo') {
            // Vimeo autoplay is handled in the URL, but we can try to trigger it
            const iframe = videoContainer.querySelector('iframe');
            if (iframe && typeof Vimeo !== 'undefined') {
                try {
                    const player = new Vimeo.Player(iframe);
                    player.play().catch(err => {
                        console.warn('Error playing Vimeo video:', err);
                    });
                } catch (err) {
                    console.warn('Error initializing Vimeo player:', err);
                }
            }
        }
    };
    
    // Modified function to show video and play it after content is revealed
    const showVideoWhenHeaderReady = () => {
        // Check if loader exists
        const loaderEl = document.querySelector('.loader');
        const hasLoader = loaderEl !== null;
        
        // Check if video exists
        const hasVideo = videoType && (videoSrc || videoId);
        
        // Function to wait for content reveal and then show video
        const waitForContentReveal = () => {
            // Wait for banner content (text and buttons) to be revealed
            document.addEventListener('bannerContentRevealed', () => {
                showAndPlayVideo();
            }, { once: true });
            
            // Fallback: if event doesn't fire, show video after a delay
            setTimeout(() => {
                if (videoContainer.style.display === 'none' || 
                    (videoTypeLower === 'mp4' && videoElement && videoElement.paused)) {
                    showAndPlayVideo();
                }
            }, FALLBACK_TIMEOUT + 2000); // Extra time for animations
        };
        
        // If no loader but banner has spline
        if (!hasLoader && hasValidBannerSpline) {
            // If video exists, wait for spline to settle, then wait for content reveal
            if (hasVideo) {
                // Wait for spline to settle first
                const handleSplineSettled = () => {
                    // After spline settles, wait for content reveal
                    waitForContentReveal();
                };
                
                // Set up event listener early to catch the event
                document.addEventListener('splineSettled', handleSplineSettled, { once: true });
                
                // Fallback: if splineSettled doesn't fire, wait a bit then proceed
                setTimeout(() => {
                    if (videoContainer.style.display === 'none') {
                        waitForContentReveal();
                    }
                }, FALLBACK_TIMEOUT);
            }
            // If no video, spline will remain visible (handled in banner() function)
            return;
        }
        
        // If no loader and no spline, wait for content reveal directly
        if (!hasLoader && !hasValidBannerSpline) {
            waitForContentReveal();
            return;
        }
        
        // If loader exists, wait for header opacity first, then wait for content reveal
        const header = document.querySelector('.header');
        if (!header) {
            // If no header, wait for content reveal then show video
            waitForContentReveal();
            return;
        }
        
        // Cache computed style function
        const getHeaderOpacity = () => parseFloat(window.getComputedStyle(header).opacity);
        
        // Check header opacity, then wait for content reveal
        const checkHeaderOpacity = () => {
            if (getHeaderOpacity() >= 1) {
                // Header is ready, now wait for content reveal
                waitForContentReveal();
            } else {
                // Check again after a short delay
                setTimeout(checkHeaderOpacity, PLAY_DELAY);
            }
        };
        
        // Start checking
        checkHeaderOpacity();
        
        // Also listen for GSAP animation completion if available
        if (hasGSAP) {
            let observerDisconnected = false;
            const disconnectObserver = () => {
                if (!observerDisconnected) {
                    observer.disconnect();
                    observerDisconnected = true;
                }
            };
            
            // Use MutationObserver to watch for opacity changes
            const observer = new MutationObserver(() => {
                if (getHeaderOpacity() >= 1) {
                    // Header is ready, now wait for content reveal
                    waitForContentReveal();
                    disconnectObserver();
                }
            });
            
            observer.observe(header, {
                attributes: true,
                attributeFilter: ['style', 'class']
            });
            
            // Fallback: check after timeout
            setTimeout(() => {
                if (getHeaderOpacity() >= 1) {
                    waitForContentReveal();
                }
                disconnectObserver();
            }, FALLBACK_TIMEOUT);
        }
    };
    
    switch (videoTypeLower) {
        case 'mp4':
            if (!videoSrc) return;
            videoElement = document.createElement('video');
            videoElement.src = videoSrc;
            videoElement.autoplay = true;
            videoElement.loop = true;
            videoElement.muted = true;
            videoElement.playsInline = true;
            videoElement.preload = 'auto';
            videoElement.setAttribute('webkit-playsinline', 'true');
            videoElement.setAttribute('x-webkit-airplay', 'allow');
            
            // Prevent video controls and interactions
            videoElement.controls = false;
            videoElement.style.pointerEvents = 'none';
            
            // Handle video errors gracefully
            videoElement.addEventListener('error', () => {
                console.warn('Video failed to load:', videoSrc);
                videoContainer.style.display = 'none';
            }, { once: true });
            
            videoContainer.appendChild(videoElement);
            showVideoWhenHeaderReady();
            break;
            
        case 'youtube':
            if (!videoId) return;
            
            // Cache callback function
            const onYouTubeReady = (player) => {
                youtubePlayer = player;
                showVideoWhenHeaderReady();
            };
            
            // Load YouTube IFrame API if not already loaded
            if (typeof YT === 'undefined') {
                // Check if script is already being loaded
                if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
                    const tag = document.createElement('script');
                    tag.src = 'https://www.youtube.com/iframe_api';
                    const firstScriptTag = document.getElementsByTagName('script')[0];
                    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
                }
                
                // Wait for API to load (handle multiple calls)
                const originalCallback = window.onYouTubeIframeAPIReady;
                window.onYouTubeIframeAPIReady = () => {
                    if (originalCallback) originalCallback();
                    createYouTubePlayer(videoId, videoContainer, onYouTubeReady);
                };
                
                // Fallback: if API doesn't load, use direct iframe embed
                setTimeout(() => {
                    if (typeof YT === 'undefined' && !videoContainer.querySelector('iframe')) {
                        createYouTubePlayer(videoId, videoContainer, onYouTubeReady);
                    }
                }, 2000);
            } else {
                createYouTubePlayer(videoId, videoContainer, onYouTubeReady);
            }
            break;
            
        case 'vimeo':
            if (!videoId) return;
            
            // Load Vimeo Player API if not already loaded
            if (typeof Vimeo === 'undefined') {
                const tag = document.createElement('script');
                tag.src = 'https://player.vimeo.com/api/player.js';
                tag.onload = () => {
                    createVimeoPlayer(videoId, videoContainer);
                    showVideoWhenHeaderReady();
                };
                document.head.appendChild(tag);
            } else {
                createVimeoPlayer(videoId, videoContainer);
                showVideoWhenHeaderReady();
            }
            break;
    }
}

/**
 * Create YouTube player
 */
function createYouTubePlayer(videoId, container, onPlayerReady) {
    // Cache origin
    const origin = window.location.origin;
    
    // Try to use YouTube IFrame API if available
    if (typeof YT !== 'undefined' && YT.Player) {
        const playerDiv = document.createElement('div');
        playerDiv.id = 'youtube-player-' + Date.now();
        container.appendChild(playerDiv);
        
        const player = new YT.Player(playerDiv.id, {
            videoId: videoId,
            playerVars: {
                autoplay: 0, // Don't autoplay immediately - we'll trigger it when displayed
                mute: 1,
                loop: 1,
                playlist: videoId,
                controls: 0,
                showinfo: 0,
                rel: 0,
                modestbranding: 1,
                playsinline: 1,
                enablejsapi: 1,
                origin: origin
            },
            events: {
                onReady: (event) => {
                    if (onPlayerReady) {
                        onPlayerReady(event.target);
                    }
                },
                onError: (event) => {
                    console.warn('YouTube player error:', event.data);
                }
            }
        });
    } else {
        // Fallback: use direct iframe embed (will rely on URL autoplay)
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=0&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&enablejsapi=1&origin=${origin}`;
        iframe.allow = 'autoplay; encrypted-media';
        iframe.allowFullscreen = false;
        container.appendChild(iframe);
        
        // For direct iframe, we can't control playback programmatically
        // So we'll rely on the URL autoplay when the container becomes visible
        if (onPlayerReady) {
            onPlayerReady(null);
        }
    }
}

/**
 * Create Vimeo player
 */
function createVimeoPlayer(videoId, container) {
    const iframe = document.createElement('iframe');
    iframe.src = `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1&loop=1&background=1&controls=0&responsive=1`;
    iframe.allow = 'autoplay; fullscreen';
    iframe.allowFullscreen = true;
    container.appendChild(iframe);
}

export function banner() {
    const bannerSection = document.querySelector('.banner') || document.querySelector('.banner-inner');
    const counterBlock = document.querySelector('.counter-block');
    const bannerTitle = document.querySelector('.banner .wave-text') || document.querySelector('.banner-inner .wave-text');
    const bannerSplineContainers = document.querySelectorAll('.banner-spline-container');
    
    if (!bannerSection) return;
    
    // Cache globals and constants
    const hasGSAP = typeof gsap !== 'undefined';
    
    // Set counter-block opacity to 0 initially (if it exists)
    if (counterBlock) {
        if (hasGSAP) {
            gsap.set(counterBlock, { opacity: 0 });
        } else {
            counterBlock.style.opacity = '0';
        }
    }
    const hasScrollTrigger = typeof ScrollTrigger !== 'undefined';
    const docEl = document.documentElement;
    const SCROLL_STOP_DELAY = 150;
    const ANIMATION_THRESHOLD = 1025;
    
    // Optimize Spline for smooth scrolling
    const optimizeSplineForScroll = () => {
        if (bannerSplineContainers.length === 0) return;
        
        bannerSplineContainers.forEach(bannerSplineContainer => {
            const splineViewer = bannerSplineContainer.querySelector('spline-viewer');
            if (!splineViewer) return;
            
            // Reduce Spline frame rate during scroll for better performance
            let isScrolling = false;
            let scrollTimeout = null;
            const setAttribute = splineViewer.setAttribute;
            const removeAttribute = splineViewer.removeAttribute;
            
            const handleScroll = () => {
                if (!isScrolling) {
                    isScrolling = true;
                    // Reduce quality/performance during scroll
                    if (setAttribute) {
                        setAttribute.call(splineViewer, 'loading', 'lazy');
                    }
                }
                
                clearTimeout(scrollTimeout);
                scrollTimeout = setTimeout(() => {
                    isScrolling = false;
                    // Restore normal quality after scroll stops
                    if (removeAttribute) {
                        removeAttribute.call(splineViewer, 'loading');
                    }
                }, SCROLL_STOP_DELAY);
            };
            
            // Throttle scroll events
            let ticking = false;
            const throttledScroll = () => {
                if (!ticking) {
                    window.requestAnimationFrame(() => {
                        handleScroll();
                        ticking = false;
                    });
                    ticking = true;
                }
            };
            
            window.addEventListener('scroll', throttledScroll, { passive: true });
        });
    };
    
    // Optimize Spline after it loads
    document.addEventListener('splineSettled', optimizeSplineForScroll, { once: true });
    
    // Load banner splines if no loader exists
    const loaderEl = document.querySelector('.loader');
    if (!loaderEl && bannerSplineContainers.length > 0) {
        // Check if video exists by checking video container and banner attributes
        const videoContainer = document.getElementById('banner-video-container');
        const videoType = bannerSection.getAttribute('data-video-type');
        const videoSrc = bannerSection.getAttribute('data-video-src');
        const videoId = bannerSection.getAttribute('data-video-id');
        const hasVideo = videoContainer && videoType && (videoSrc || videoId);
        
        // Track how many splines need to be loaded
        let splinesToLoad = 0;
        let splinesLoaded = 0;
        const allSplinesLoaded = () => {
            splinesLoaded++;
            if (splinesLoaded >= splinesToLoad) {
                // Dispatch event when all Splines have settled
                // If video exists, the video handler will wait 5 seconds before showing video
                // If no video, spline will remain visible
                document.dispatchEvent(new CustomEvent('splineSettled'));
            }
        };
        
        // Initialize each spline container
        bannerSplineContainers.forEach((bannerSplineContainer) => {
            const bannerSplineUrl = bannerSplineContainer.getAttribute('data-spline-url');
            const hasValidBannerSpline = bannerSplineUrl && bannerSplineUrl.trim() !== '';
            
            if (hasValidBannerSpline) {
                // Check if spline viewer already exists
                const existingViewer = bannerSplineContainer.querySelector('spline-viewer');
                
                if (!existingViewer) {
                    splinesToLoad++;
                    // Mark as loading to prevent duplicate loads
                    bannerSplineContainer.setAttribute('data-spline-loading', 'true');
                    
                    // Load the banner spline
                    import('./spline.js').then(({ createSplineViewer }) => {
                        createSplineViewer(bannerSplineContainer, bannerSplineUrl).then((splineViewer) => {
                            // Remove loading flag
                            bannerSplineContainer.removeAttribute('data-spline-loading');
                            if (splineViewer && hasGSAP) {
                                // Make spline visible
                                gsap.set(bannerSplineContainer, {
                                    opacity: 1,
                                    visibility: 'visible'
                                });
                                bannerSplineContainer.style.pointerEvents = 'auto';
                                
                                // Wait for spline to load, then dispatch splineSettled event
                                // Use a combination of load event and timeout as fallback
                                const handleSplineLoad = () => {
                                    // Add settling animation to Spline
                                    gsap.fromTo(bannerSplineContainer,
                                        {
                                            scale: 1.02,
                                            opacity: 0.98
                                        },
                                        {
                                            scale: 1,
                                            opacity: 1,
                                            duration: 0.6,
                                            ease: 'power2.out',
                                            onComplete: () => {
                                                allSplinesLoaded();
                                            }
                                        }
                                    );
                                };
                                
                                // Listen for load event on spline-viewer
                                splineViewer.addEventListener('load', handleSplineLoad, { once: true });
                                
                                // Fallback: dispatch after a delay if load event doesn't fire
                                setTimeout(() => {
                                    if (!splineViewer.hasAttribute('data-settled')) {
                                        splineViewer.setAttribute('data-settled', 'true');
                                        handleSplineLoad();
                                    }
                                }, 2000);
                            } else if (splineViewer) {
                                // No GSAP, just make visible and dispatch event
                                bannerSplineContainer.style.opacity = '1';
                                bannerSplineContainer.style.visibility = 'visible';
                                bannerSplineContainer.style.pointerEvents = 'auto';
                                
                                setTimeout(() => {
                                    allSplinesLoaded();
                                }, 1000);
                            }
                        }).catch(() => {
                            // Remove loading flag on error
                            bannerSplineContainer.removeAttribute('data-spline-loading');
                            allSplinesLoaded(); // Count as loaded even on error
                        });
                    });
                } else {
                    // Spline viewer already exists, just make sure it's visible
                    if (hasGSAP) {
                        gsap.set(bannerSplineContainer, {
                            opacity: 1,
                            visibility: 'visible'
                        });
                    } else {
                        bannerSplineContainer.style.opacity = '1';
                        bannerSplineContainer.style.visibility = 'visible';
                    }
                    bannerSplineContainer.style.pointerEvents = 'auto';
                    
                    // Count as already loaded
                    splinesToLoad++;
                    allSplinesLoaded();
                }
            }
        });
        
        // If no splines to load but video exists, dispatch event immediately
        if (splinesToLoad === 0 && hasVideo) {
            document.dispatchEvent(new CustomEvent('splineSettled'));
        }
    }
    
    // Set CSS custom property for header height
    const header = document.querySelector('.header');
    
    // If no loader exists, show header initially
    if (!loaderEl && header && hasGSAP) {
        gsap.set(header, { opacity: 1 });
    } else if (!loaderEl && header) {
        header.style.opacity = '1';
    }
    
    const updateHeaderHeight = () => {
        if (header) {
            const headerHeight = header.getBoundingClientRect().height;
            docEl.style.setProperty('--header-height', `${headerHeight}px`);
        } else {
            docEl.style.setProperty('--header-height', '0px');
        }
    };
    
    // Set initial header height
    updateHeaderHeight();
    
    // Initialize background video
    initBannerVideo();
    
    // Hide banner title initially until Spline settles and animation is ready
    if (bannerTitle && hasGSAP) {
        gsap.set(bannerTitle, { opacity: 0, visibility: 'hidden' });
        
        // Show title when Spline has settled (text reveal will handle the animation)
        const showTitle = () => {
            gsap.set(bannerTitle, { opacity: 1, visibility: 'visible' });
        };
        
        // Wait for Spline to settle before showing title
        document.addEventListener('splineSettled', showTitle, { once: true });
    }
    
    // Handle button reveal after title animation completes
    const btnGroup = bannerSection.querySelector('.btn-group');
    if (btnGroup && hasGSAP) {
        // Set button to opacity 0 initially
        gsap.set(btnGroup, { opacity: 0, visibility: 'hidden' });
        
        // Listen for text reveal completion on the banner title
        const handleTextRevealComplete = () => {
            // Animate button in after title reveal completes
            gsap.to(btnGroup, {
                opacity: 1,
                visibility: 'visible',
                duration: 0.8,
                ease: 'power2.out',
                delay: 0, // Small delay after title completes
                onComplete: () => {
                    // Dispatch event when button reveal completes
                    document.dispatchEvent(new CustomEvent('bannerContentRevealed'));
                }
            });
        };
        
        // Listen for text reveal completion event from the banner title
        if (bannerTitle) {
            bannerTitle.addEventListener('textRevealComplete', handleTextRevealComplete, { once: true });
        } else {
            // If no title, show button immediately and dispatch event
            gsap.to(btnGroup, {
                opacity: 1,
                visibility: 'visible',
                duration: 0.8,
                ease: 'power2.out',
                onComplete: () => {
                    document.dispatchEvent(new CustomEvent('bannerContentRevealed'));
                }
            });
        }
    } else if (btnGroup) {
        // Fallback if GSAP is not available
        btnGroup.style.opacity = '0';
        btnGroup.style.visibility = 'hidden';
        
        if (bannerTitle) {
            const handleTextRevealComplete = () => {
                setTimeout(() => {
                    btnGroup.style.transition = 'opacity 0.8s ease-out, visibility 0.8s ease-out';
                    btnGroup.style.opacity = '1';
                    btnGroup.style.visibility = 'visible';
                    // Dispatch event after transition completes (0.8s)
                    setTimeout(() => {
                        document.dispatchEvent(new CustomEvent('bannerContentRevealed'));
                    }, 800);
                }, 300);
            };
            
            bannerTitle.addEventListener('textRevealComplete', handleTextRevealComplete, { once: true });
        } else {
            // If no title, show button immediately
            setTimeout(() => {
                btnGroup.style.transition = 'opacity 0.8s ease-out, visibility 0.8s ease-out';
                btnGroup.style.opacity = '1';
                btnGroup.style.visibility = 'visible';
                setTimeout(() => {
                    document.dispatchEvent(new CustomEvent('bannerContentRevealed'));
                }, 800);
            }, 100);
        }
    } else {
        // If no button group, dispatch event after text reveal completes
        if (bannerTitle) {
            bannerTitle.addEventListener('textRevealComplete', () => {
                document.dispatchEvent(new CustomEvent('bannerContentRevealed'));
            }, { once: true });
        } else {
            // If no title and no button, dispatch immediately
            setTimeout(() => {
                document.dispatchEvent(new CustomEvent('bannerContentRevealed'));
            }, 100);
        }
    }
    
    // If no loader exists, dispatch loaderCompleted event after spline settles
    // This ensures text reveal animation works when there's no loader
    // The text reveal component requires both loaderCompleted and splineSettled events
    if (!loaderEl) {
        let hasValidBannerSpline = false;
        bannerSplineContainers.forEach(container => {
            const bannerSplineUrl = container.getAttribute('data-spline-url');
            if (bannerSplineUrl && bannerSplineUrl.trim() !== '') {
                hasValidBannerSpline = true;
            }
        });
        
        if (hasValidBannerSpline) {
            // If spline exists, wait for it to settle before dispatching loaderCompleted
            const handleSplineSettledForLoader = () => {
                // Dispatch loaderCompleted event so text reveal can work
                // Use a small delay to ensure spline animation is fully complete
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        document.dispatchEvent(new CustomEvent('loaderCompleted'));
                    });
                });
            };
            
            document.addEventListener('splineSettled', handleSplineSettledForLoader, { once: true });
        } else {
            // If no spline, dispatch loaderCompleted immediately (after a short delay for page load)
            // Also dispatch splineSettled so text reveal can work
            setTimeout(() => {
                document.dispatchEvent(new CustomEvent('splineSettled'));
                document.dispatchEvent(new CustomEvent('loaderCompleted'));
            }, 100);
        }
    }
    
    let prePinTrigger = null;
    let pinTrigger = null;
    let secondTrigger = null;
    
    const resetCounterBlock = () => {
        if (!counterBlock) return;
        if (hasGSAP) {
            gsap.set(counterBlock, { 
                clearProps: "all",
                y: 0,
                opacity: 1
            });
        }
    };
    
    const cleanupScrollTriggers = () => {
        if (prePinTrigger) prePinTrigger.kill();
        if (pinTrigger) pinTrigger.kill();
        if (secondTrigger) secondTrigger.kill();
        prePinTrigger = pinTrigger = secondTrigger = null;
        if (hasScrollTrigger) {
            ScrollTrigger.refresh();
        }
    };
    
    // Check if screen size is large enough for animations
    const shouldEnableAnimations = () => {
        return window.innerWidth >= ANIMATION_THRESHOLD;
    };
    
    const initGSAPAnimations = () => {
        // Skip animations if counter block doesn't exist (e.g., banner-inner)
        if (!counterBlock) {
            return;
        }
        
        // Skip animations on screens smaller than threshold
        if (!shouldEnableAnimations()) {
            resetCounterBlock();
            return;
        }
        
        if (!hasGSAP || !hasScrollTrigger) {
            resetCounterBlock();
            return;
        }
        
        gsap.registerPlugin(ScrollTrigger);
        
        // Cache header reference and getHeaderHeight function
        const headerEl = header;
        const getHeaderHeight = () => {
            return headerEl ? headerEl.getBoundingClientRect().height : 0;
        };
        
        // Apply performance optimizations to counter-block
        gsap.set(counterBlock, {
            willChange: "transform, opacity",
            force3D: true,
            backfaceVisibility: "hidden"
        });
        
        const setCounterY = gsap.quickSetter(counterBlock, "y", "px");
        const setCounterOpacity = gsap.quickSetter(counterBlock, "opacity");
        
        let counterBlockNaturalTop = null;
        let viewportHeight = window.innerHeight;
        const EXTRA_OFFSET_MULTIPLIER = 0.1;
        const UNPIN_PROGRESS = 0.77;
        const OFFSET_THRESHOLD = 1;
        
        // Helper to update viewport height
        const updateViewportHeight = () => {
            viewportHeight = window.innerHeight;
        };
        
        const measureNaturalPosition = () => {
            if (counterBlockNaturalTop === null) {
                const currentY = gsap.getProperty(counterBlock, "y") || 0;
                gsap.set(counterBlock, { y: 0 });
                void counterBlock.offsetHeight;
                
                const bannerRect = bannerSection.getBoundingClientRect();
                const counterBlockRect = counterBlock.getBoundingClientRect();
                counterBlockNaturalTop = counterBlockRect.top - bannerRect.top;
                
                gsap.set(counterBlock, { y: currentY });
            }
            return counterBlockNaturalTop;
        };
        
        const calculateInitialOffset = () => {
            updateViewportHeight();
            const naturalTop = measureNaturalPosition();
            const extraOffset = viewportHeight * EXTRA_OFFSET_MULTIPLIER;
            return Math.max(0, viewportHeight - naturalTop + extraOffset);
        };
        
        let initialOffset = calculateInitialOffset();
        
        // Cache calculations to avoid repeated getBoundingClientRect calls
        let cachedBannerHeight = null;
        const getBannerHeight = () => {
            if (cachedBannerHeight === null) {
                cachedBannerHeight = bannerSection.getBoundingClientRect().height;
            }
            return cachedBannerHeight;
        };
        
        const checkInitialState = () => {
            // Only check initial state when not actively pinned to prevent jumps
            // Check if any trigger is active to avoid conflicts
            if ((pinTrigger?.isActive) || (prePinTrigger?.isActive) || (secondTrigger?.isActive)) {
                return; // Don't interfere with active scroll triggers
            }
            
            const bannerRect = bannerSection.getBoundingClientRect();
            updateViewportHeight();
            const headerHeight = getHeaderHeight();
            const scrollDuringPin = viewportHeight * UNPIN_PROGRESS;
            const scrollAfterPin = viewportHeight * (1 - UNPIN_PROGRESS);
            const bannerTop = bannerRect.top;
            const bannerBottom = bannerRect.bottom;
            
            if (bannerTop <= headerHeight) {
                const scrollDistance = Math.abs(bannerTop - headerHeight);
                
                if (scrollDistance >= scrollDuringPin + scrollAfterPin) {
                    setCounterY(0);
                    setCounterOpacity(1);
                } else if (scrollDistance >= scrollDuringPin) {
                    const progressInSecondZone = Math.min(1, Math.max(0, (scrollDistance - scrollDuringPin) / scrollAfterPin));
                    const yAtUnpin = initialOffset * (1 - UNPIN_PROGRESS);
                    setCounterY(yAtUnpin * (1 - progressInSecondZone));
                    setCounterOpacity(1);
                } else {
                    const scrollProgress = Math.min(1, Math.max(0, scrollDistance / scrollDuringPin));
                    setCounterY(initialOffset * (1 - scrollProgress * UNPIN_PROGRESS));
                    setCounterOpacity(0.7 + (scrollProgress * 0.3));
                }
            } else if (bannerBottom > headerHeight && bannerTop < viewportHeight) {
                const bannerHeight = getBannerHeight();
                const progress = Math.max(0, Math.min(1, (viewportHeight - bannerTop) / (viewportHeight + bannerHeight)));
                const startY = initialOffset * 1.1;
                setCounterY(startY * (1 - progress * 0.3));
                setCounterOpacity(progress * 0.7);
            } else {
                setCounterY(initialOffset * 1.1);
                setCounterOpacity(0);
            }
        };
        
        const INITIAL_Y_MULTIPLIER = 1.1;
        setCounterY(initialOffset * INITIAL_Y_MULTIPLIER);
        setCounterOpacity(0);
        checkInitialState();
        
        // Use numeric scrub value for smoother animation (lower = smoother but more CPU intensive)
        const scrubValue = 0.5;
        
        // Cache refresh handler
        const invalidateCache = () => {
            counterBlockNaturalTop = null;
            cachedBannerHeight = null;
            updateViewportHeight();
        };
        
        // Cache constants for calculations
        const PREPIN_START_Y_MULT = INITIAL_Y_MULTIPLIER;
        const PREPIN_OPACITY_MULT = 0.3;
        const PREPIN_OPACITY_BASE = 0.7;
        const PIN_OPACITY_BASE = 0.7;
        const PIN_OPACITY_RANGE = 0.3;
        
        prePinTrigger = ScrollTrigger.create({
            trigger: bannerSection,
            start: () => {
                const headerHeight = getHeaderHeight();
                return `top bottom-=${headerHeight}`;
            },
            end: () => {
                const headerHeight = getHeaderHeight();
                return `top top+=${headerHeight}`;
            },
            scrub: scrubValue,
            invalidateOnRefresh: true,
            onRefresh: invalidateCache,
            onUpdate: (self) => {
                // Smooth scrubbed animation
                const startY = initialOffset * PREPIN_START_Y_MULT;
                setCounterY(startY * (1 - self.progress * PREPIN_OPACITY_MULT));
                setCounterOpacity(self.progress * PREPIN_OPACITY_BASE);
            },
            onLeave: () => {
                // Match pinTrigger onEnter state for smooth transition
                setCounterY(initialOffset);
                setCounterOpacity(PIN_OPACITY_BASE);
            },
            onLeaveBack: () => {
                // Smooth transition when scrolling back up
                setCounterY(initialOffset * PREPIN_START_Y_MULT);
                setCounterOpacity(0);
            }
        });
        
        pinTrigger = ScrollTrigger.create({
            trigger: bannerSection,
            start: () => {
                const headerHeight = getHeaderHeight();
                return `top top+=${headerHeight}`;
            },
            end: () => `+=${viewportHeight * UNPIN_PROGRESS}`,
            pin: true,
            pinSpacing: true,
            scrub: scrubValue,
            anticipatePin: 1,
            invalidateOnRefresh: true,
            onRefresh: () => {
                // Only invalidate cache, don't recalculate during active pinning
                invalidateCache();
                // Recalculate offset but don't apply immediately - let onUpdate handle it
                const newOffset = calculateInitialOffset();
                // Only update if significantly different to prevent micro-jumps
                if (Math.abs(newOffset - initialOffset) > OFFSET_THRESHOLD) {
                    initialOffset = newOffset;
                }
            },
            immediateRender: false,
            onUpdate: (self) => {
                // Always use onUpdate for smooth scrubbed animation
                const progress = self.progress * UNPIN_PROGRESS;
                setCounterY(initialOffset * (1 - progress));
                setCounterOpacity(PIN_OPACITY_BASE + (progress * PIN_OPACITY_RANGE));
            },
            onLeave: () => {
                // Calculate final position based on progress
                const yAtUnpin = initialOffset * (1 - UNPIN_PROGRESS);
                setCounterY(yAtUnpin);
                setCounterOpacity(1);
            },
            onLeaveBack: () => {
                // Smooth transition back - match prePinTrigger end state
                setCounterY(initialOffset);
                setCounterOpacity(PIN_OPACITY_BASE);
            }
        });
        
        // Use requestAnimationFrame to ensure smooth initialization
        requestAnimationFrame(() => {
            ScrollTrigger.refresh();
            // Only check initial state if not already in pinned zone
            const bannerRect = bannerSection.getBoundingClientRect();
            const headerHeight = getHeaderHeight();
            if (bannerRect.top > headerHeight) {
                checkInitialState();
            }
        });
        
        secondTrigger = ScrollTrigger.create({
            trigger: bannerSection,
            start: () => {
                const headerHeight = getHeaderHeight();
                const scrollDuringPin = viewportHeight * UNPIN_PROGRESS;
                return `top+=${scrollDuringPin} top+=${headerHeight}`;
            },
            end: () => `+=${viewportHeight * (1 - UNPIN_PROGRESS)}`,
            scrub: scrubValue,
            invalidateOnRefresh: true,
            onRefresh: () => {
                // Invalidate cache but don't cause jumps
                invalidateCache();
                const newOffset = calculateInitialOffset();
                // Only update if significantly different to prevent micro-jumps
                if (Math.abs(newOffset - initialOffset) > OFFSET_THRESHOLD) {
                    initialOffset = newOffset;
                }
            },
            onUpdate: (self) => {
                // Smooth scrubbed animation - always use onUpdate
                const yAtUnpin = initialOffset * (1 - UNPIN_PROGRESS);
                const targetY = yAtUnpin * (1 - self.progress);
                // Ensure we don't go below 0 to prevent jumps
                setCounterY(Math.max(0, targetY));
                setCounterOpacity(1);
            },
            onLeave: () => {
                // Final position when leaving forward
                setCounterY(0);
                setCounterOpacity(1);
            },
            onLeaveBack: () => {
                // Match pinTrigger onLeave state for smooth transition
                const yAtUnpin = initialOffset * (1 - UNPIN_PROGRESS);
                setCounterY(yAtUnpin);
                setCounterOpacity(1);
            }
        });
    };
    
    let resizeTimeout = null;
    let previousScreenSize = shouldEnableAnimations();
    const RESIZE_DELAY = 150;
    
    const handleResize = () => {
        if (resizeTimeout) clearTimeout(resizeTimeout);
        
        resizeTimeout = setTimeout(() => {
            // Update header height CSS variable
            updateHeaderHeight();
            
            const currentScreenSize = shouldEnableAnimations();
            
            // If screen size crossed the threshold, reinitialize animations
            if (currentScreenSize !== previousScreenSize) {
                // Clean up existing triggers
                cleanupScrollTriggers();
                // Reset counter block to normal state
                resetCounterBlock();
                // Reinitialize animations if screen is large enough
                if (currentScreenSize) {
                    initGSAPAnimations();
                }
                previousScreenSize = currentScreenSize;
            } else if (hasScrollTrigger && currentScreenSize) {
                // Only refresh if animations are enabled
                ScrollTrigger.refresh();
            }
        }, RESIZE_DELAY);
    };
    
    initGSAPAnimations();
    window.addEventListener('resize', handleResize, { passive: true });
}
